import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronRight, FaChevronLeft } from 'react-icons/fa';
import AsyncSelect from 'react-select/async';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import useAuth from '../../../hooks/useAuth';
import { components as defaultComponents } from 'react-select';
import API_URLS from '../../../constants/apiConstants';
import SchoolStorage from '../../../utils/localStorage';

export const SchoolsDropdown = () => {
  const [options, setOptions] = useState([]);
  const { i18n } = useTranslation();
  const direction = i18n.dir();
  const axiosPrivate = useAxiosPrivate();
  const { auth, school, setSchool, schools, setSchools } = useAuth();

  const setSchoolGloballyAndInStorage = useCallback((schoolId, schoolName , schoolLogo) => {
    setSchool({ id: schoolId, name: schoolName, logo: schoolLogo });
    SchoolStorage.setSchool(schoolId, schoolName, schoolLogo);
  }, [setSchool]);

  useEffect(() => {
    const fetchOptions = async () => {
      
      // If the schools are stored in the global state don't call API
      if (schools?.length > 0) {
        setOptions(schools);
        return;
      }

      try {
        const api_url = API_URLS.getSchools(auth.id);
        const response = await axiosPrivate.get(api_url);
        const data = response.data.nurseries.map(school => ({
          value: school.id,
          label: school.Name,
          logo: school.LogoImage,
        }));

        // set school if global state is null
        if (!school.id || !school.name) {
          setSchoolGloballyAndInStorage(data[0].value, data[0].label,data[0].logo);
        }

        setOptions(data);
        setSchools(data);
      } catch (error) {
        console.error("Error loading options", error);
      }
    };

    fetchOptions();
  }, [auth.id, axiosPrivate, school.id, school.name, schools, setSchools, setSchoolGloballyAndInStorage]);

  useEffect(() => {
    const handleSchoolsUpdated = (event) => {
      const updatedSchools = event.detail?.schools;
      if (!Array.isArray(updatedSchools) || updatedSchools.length === 0) {
        return;
      }

      setOptions(updatedSchools);
      setSchools(updatedSchools);
    };

    window.addEventListener("schools:updated", handleSchoolsUpdated);
    return () => window.removeEventListener("schools:updated", handleSchoolsUpdated);
  }, [setSchools, setOptions]);

  const onChange = (option) => {
    if (!option?.value) return;

    setSchoolGloballyAndInStorage(option?.value, option?.label , option?.logo);
    // Reload
    window.location.reload();
  };

  return (
    <AsyncSelect
      cacheOptions
      defaultOptions={options}
      loadOptions={() => Promise.resolve([])} // disables search
      components={{
        ...defaultComponents,
        IndicatorSeparator: () => null,
        DropdownIndicator: (props) => (
          <defaultComponents.DropdownIndicator {...props}>
            {direction === 'ltr' ? <FaChevronRight /> : <FaChevronLeft />}
          </defaultComponents.DropdownIndicator>
        )
      }}

      value={{ value: school?.id, label: school?.name }}
      onChange={onChange}
      placeholder="Select a school"
      isClearable={false}
      isSearchable={false}
      menuPosition="absolute"
      menuPlacement="auto"
      menuPortalTarget={document.body} // Allows free positioning
      styles={{
        control: (base, state) => ({
          ...base,
          minWidth: "11rem",
          maxWidth: "15rem",
          fontSize: "1rem",
          border: "none",
          borderRadius: "0",
          boxShadow: "none",
          outline: "none",
          padding: "10px",
          "&:hover": {
            backgroundColor: "inherit",
            cursor: "pointer",
          },
        }),
        menuPortal: base => ({
          ...base,
          zIndex: 9999,
        }),
        menu: (base) => ({
          ...base,
          top: '-50px',
          left: direction === 'rtl' ? '-100%' : '100%',     // <-- Open to the right
          marginLeft: '5px',
          position: 'absolute',
        }),
        container: (base) => ({
          ...base,
          position: 'relative',
        }),
        option: (base, state) => ({
          ...base,
          fontSize: "1rem",
          backgroundColor: state.isSelected
            ? "var(--s-purple-opacity-color)"
            : state.isFocused
            ? "#eee"
            : "white",
          cursor: "pointer",
        }),
      }}
    />
  );
};
